import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { GeoCoordinates } from '../../models/serializable/geo-coordinates.js';
import { Language } from '../../models/serializable/language.js';
import { LocalizedRichDescription } from '../../models/serializable/localized-rich-description.js';
import { LocalizedString } from '../../models/serializable/localized-string.js';
import { PhotoReference } from '../../models/serializable/file-reference/photo-reference.js';
import { RichDescription } from '../../models/serializable/rich-description.js';
import { toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';

export class MapViewComponent extends RenderableComponent {
  static readonly TYPE_ID: string = 'e16ea98b2124424881abb8989bcb3f14';
  static readonly SCHEMA_VERSION: number = 4;
  static readonly DEFAULT_RENDER_ORDER = 11;

  constructor(
    public titles: LocalizedString,
    public richDescriptions: LocalizedRichDescription,
    public coordinates: GeoCoordinates | null,
    public mapPreview: PhotoReference | null,
    public renderOrder: number,
    public isRenderingEnabled: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return MapViewComponent.TYPE_ID;
  }

  get $removable(): boolean {
    return false;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(MapViewComponent.SCHEMA_VERSION);
  }

  static empty(): MapViewComponent {
    return new MapViewComponent(
        LocalizedString.empty(),
        LocalizedRichDescription.empty(),
        null,
        null,
        MapViewComponent.DEFAULT_RENDER_ORDER,
        true,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): MapViewComponent {
    return new MapViewComponent(
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'titles')),
        context.fromData<LocalizedRichDescription>(MapUtils.get<SerializedData>(map, 'richDescriptions')),
        context.fromDataOrNull<GeoCoordinates>(MapUtils.getOrNull<SerializedData>(map, 'coordinates')),
        context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'mapPreview')),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'titles': context.toData(this.titles),
      'richDescriptions': context.toData(this.richDescriptions),
      'coordinates': context.toDataOrNull(this.coordinates),
      'mapPreview': context.toDataOrNull(this.mapPreview),
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
    };
  }

  clone(): this {
    return new MapViewComponent(
        this.titles.clone(),
        this.richDescriptions.clone(),
        this.coordinates?.clone() ?? null,
        this.mapPreview?.clone() ?? null,
        this.renderOrder,
        this.isRenderingEnabled
    ) as this;
  }

  isSame(other: MapViewComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.titles, this.titles) &&
      isSameData(other.richDescriptions, this.richDescriptions) &&
      isSameData(other.coordinates, this.coordinates) &&
      isSameData(other.mapPreview, this.mapPreview) &&
      other.renderOrder === this.renderOrder &&
      other.isRenderingEnabled === this.isRenderingEnabled;
  }
}

export class MapViewComponentBuilder extends DataModelBuilder<MapViewComponent> {
  build(context: SerializationContext, map: SerializedData): MapViewComponent {
    return MapViewComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(MapViewComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(MapViewComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `directions` of type [Map<Language, RichDescription>]
    // was replaced with `richDescriptions` of type [LocalizedRichDescription].
    if(version < 2) {
      const directions = MapUtils.getMap<Language, RichDescription>(map, 'directions', (k, v) => {
        return {
          key: toEnumFromString(k, Language),
          value: context.fromData<RichDescription>(v as SerializedData),
        };
      });

      MapUtils.set(map, 'richDescriptions', context.toData(LocalizedRichDescription.localized(directions)));
      version = 2;
    }

    // In this version `titles` of type [LocalizedString] was introduced.
    if(version < 3) {
      MapUtils.set(map, 'titles', context.toData(LocalizedString.empty()));
      version = 3;
    }

    // In this version `MapDirectionsComponent` was renamed to
    // `MapViewComponent` and converted to type [RenderableComponent].
    // Also `coordinates` of type [GeoCoordinates] and `mapPreview` of type
    // [PhotoReference] were introduced.
    if(version < 4) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', MapViewComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
